import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../lib/api';
import { toast } from 'sonner';

interface Template {
  id: string;
  name: string;
  status: 'draft' | 'active' | 'archived';
  template_type: 'email' | 'sms';
}

interface TemplateActionButtonsProps {
  template: Template;
  onPreview?: (templateId: string) => void;
  size?: 'sm' | 'md' | 'lg';
  showLabels?: boolean;
}

export const TemplateActionButtons: React.FC<TemplateActionButtonsProps> = ({
  template,
  onPreview,
  size = 'md',
  showLabels = false,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Size classes for buttons
  const sizeClasses = {
    sm: 'p-1',
    md: 'p-2',
    lg: 'p-2.5',
  };

  const iconSizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  // Duplicate mutation
  const duplicateMutation = useMutation({
    mutationFn: async () => {
      const response = await api.templates.duplicate(template.id);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast.success('Template duplicated successfully');
      navigate(`/templates/${data.id}/edit`);
    },
    onError: (error: any) => {
      toast.error(`Failed to duplicate template: ${error.response?.data?.detail || error.message}`);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.templates.delete(template.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast.success('Template deleted successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete template: ${error.response?.data?.detail || error.message}`);
    },
  });

  const handleView = () => {
    if (onPreview) {
      onPreview(template.id);
    } else {
      navigate(`/templates/${template.id}`);
    }
  };

  const handleEdit = () => {
    navigate(`/templates/${template.id}/edit`);
  };

  const handleDuplicate = () => {
    duplicateMutation.mutate();
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    deleteMutation.mutate();
    setShowDeleteDialog(false);
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
  };

  // Check if delete is allowed (only for draft and archived templates)
  const canDelete = template.status === 'draft' || template.status === 'archived';

  return (
    <>
      <div className="flex items-center space-x-2">
        {/* View/Preview Button */}
        <button
          onClick={handleView}
          className={`inline-flex items-center ${sizeClasses[size]} text-gray-400 hover:text-indigo-600 transition-colors`}
          title="View template"
        >
          <EyeIcon className={iconSizes[size]} />
          {showLabels && <span className="ml-1 text-sm">View</span>}
        </button>

        {/* Edit Button */}
        <button
          onClick={handleEdit}
          className={`inline-flex items-center ${sizeClasses[size]} text-gray-400 hover:text-indigo-600 transition-colors`}
          title="Edit template"
        >
          <PencilIcon className={iconSizes[size]} />
          {showLabels && <span className="ml-1 text-sm">Edit</span>}
        </button>

        {/* Duplicate Button */}
        <button
          onClick={handleDuplicate}
          disabled={duplicateMutation.isPending}
          className={`inline-flex items-center ${sizeClasses[size]} text-gray-400 hover:text-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          title="Duplicate template"
        >
          <DocumentDuplicateIcon className={iconSizes[size]} />
          {showLabels && <span className="ml-1 text-sm">Duplicate</span>}
        </button>

        {/* Delete Button - Only show for draft/archived templates */}
        {canDelete && (
          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className={`inline-flex items-center ${sizeClasses[size]} text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
            title="Delete template"
          >
            <TrashIcon className={iconSizes[size]} />
            {showLabels && <span className="ml-1 text-sm">Delete</span>}
          </button>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <TrashIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Delete Template</h3>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-sm text-gray-500">
                Are you sure you want to delete <span className="font-medium text-gray-900">"{template.name}"</span>? 
                This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteMutation.isPending}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteMutation.isPending && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

